import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { disableIdleLockSwitch, enableIdleLockSwitch } from "../../../../globals";
import { classroomStatus } from "../../../../constants";
import BuildingsService from "../../../../services/campus/buildings.service";
import ClassroomsService from "../../../../services/campus/classrooms.service";
import { tituloEstado } from "../../../../utils/tituloEstado";
import { errorText } from "../../managment/enrollmentRequests/helpers";
import { Aviso, Campo, Ventana } from "../../managment/calendarioAcademico/piezas";

/* Ventanas de alta y edición de Aulas y Edificios (Horarios Claros, 2026-11-01): mismos campos y mismos servicios que las pantallas viejas. */

export interface Edificio { id: number; name: string; description?: string | null; }
export interface Categoria { id: number; name: string; }
export interface Estatus { id: number; name: string; }
export interface Aula {
	id: number;
	name: string;
	building_id: number;
	status_id: number;
	students_limit: number;
	session_min_hours: number;
	description?: string | null;
	categories: Categoria[];
	status?: Estatus | null;
	building?: any;
	[otro: string]: any;
}

/** El catálogo guarda las categorías en mayúsculas: se leen en Title Case. */
export const nombreCategoria = (nombre: string): string => tituloEstado(String(nombre || "").toLowerCase());

/** Lo que el servidor dice cuando no se pudo guardar o borrar, en español. */
export const motivoError = (e: any, duplicado: string, generico: string): string => {
	if (e?.response?.status === 409) return duplicado;
	if (e?.response?.status === 422 && e?.response?.data?.errors) return errorText(e);
	return generico;
};

const entero = (v: string): number | null => (/^\d+$/.test(String(v).trim()) ? Number(v) : null);

// ------------------------------------------------------------------ aula

interface BorradorAula { name: string; building_id: number; status_id: number; students_limit: string; session_min_hours: string; categories: Categoria[]; description: string; }

export const AulaVentana: React.FC<{
	campusId: number;
	aula: Aula | null;
	edificioInicial: number;
	edificios: Edificio[];
	categorias: Categoria[];
	estatuses: Estatus[];
	puedeEditar: boolean;
	puedeBorrar: boolean;
	onClose: () => void;
	onGuardada: (aviso: string) => void;
	onBorrar: (a: Aula) => void;
}> = ({ campusId, aula, edificioInicial, edificios, categorias, estatuses, puedeEditar, puedeBorrar, onClose, onGuardada, onBorrar }) => {
	const dispatch = useDispatch();
	const [b, setB] = useState<BorradorAula>(() => aula ? {
		name: aula.name, building_id: aula.building_id, status_id: aula.status_id, students_limit: String(aula.students_limit ?? 0),
		session_min_hours: String(aula.session_min_hours ?? 1), categories: aula.categories || [], description: aula.description || "",
	} : {
		name: "", building_id: edificioInicial, status_id: classroomStatus.ACTIVE, students_limit: "0", session_min_hours: "1", categories: [], description: "",
	});
	const [error, setError] = useState<string | null>(null);
	const [guardando, setGuardando] = useState(false);
	const soloLectura = !puedeEditar;
	const set = (c: Partial<BorradorAula>) => setB((v) => ({ ...v, ...c }));
	const bloqueado = guardando || soloLectura;

	const alternar = (c: Categoria) => set({ categories: b.categories.some((x) => x.id === c.id) ? b.categories.filter((x) => x.id !== c.id) : [...b.categories, c] });

	const guardar = async () => {
		if (guardando || soloLectura) return;
		const nombre = b.name.trim();
		const cupo = entero(b.students_limit);
		const horas = entero(b.session_min_hours);
		if (!nombre) { setError("Escribe el nombre del aula."); return; }
		if (cupo === null) { setError("El cupo debe ser un número entero, 0 o más."); return; }
		if (horas === null || horas < 1 || horas > 14) { setError("Las horas mínimas por sesión van de 1 a 14."); return; }
		if (b.categories.length < 1) { setError("Elige al menos una categoría."); return; }
		setGuardando(true);
		setError(null);
		dispatch(enableIdleLockSwitch());
		try {
			await ClassroomsService.saveClassroom(campusId, {
				...(aula || {}), id: aula ? aula.id : null, name: nombre, building_id: b.building_id, status_id: b.status_id,
				students_limit: cupo, session_min_hours: horas, categories: b.categories, description: b.description,
			});
			onGuardada(aula ? "Aula Guardada" : "Aula Creada");
		} catch (e) {
			setError(motivoError(e, "Ya hay un aula con ese nombre.", "No se pudo guardar el aula."));
			setGuardando(false);
		} finally {
			dispatch(disableIdleLockSwitch());
		}
	};

	return (
		<Ventana titulo={aula ? "Aula" : "Nueva Aula"} onClose={onClose} busy={guardando} pie={<>
			{aula && puedeBorrar ? <button type="button" className="btn btn-outline-secondary" disabled={guardando} onClick={() => onBorrar(aula)}>Borrar Aula</button> : null}
			<span className="calPieEspacio" />
			<button type="button" className="btn btn-outline-secondary" disabled={guardando} onClick={onClose}>{soloLectura ? "Cerrar" : "Cancelar"}</button>
			{soloLectura ? null : <button type="button" className="btn btn-primary" disabled={guardando} onClick={guardar}><FontAwesomeIcon icon={solid("check")} />&nbsp;Guardar</button>}
		</>}>
			{error ? <Aviso>{error}</Aviso> : null}
			<form onSubmit={(e) => { e.preventDefault(); guardar(); }}>
				<div className="calCampos">
					<Campo t="Nombre">
						<input className="form-control" type="text" maxLength={50} value={b.name} disabled={bloqueado} autoFocus={!aula} onChange={(e) => set({ name: e.target.value })} />
					</Campo>
					<Campo t="Edificio">
						<select className="form-select" value={b.building_id} disabled={bloqueado} onChange={(e) => set({ building_id: Number(e.target.value) })}>
							{edificios.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
						</select>
					</Campo>
					<Campo t="Cupo">
						<input className="form-control" type="number" min={0} step={1} value={b.students_limit} disabled={bloqueado} onChange={(e) => set({ students_limit: e.target.value })} />
					</Campo>
					<Campo t="Horas Mínimas por Sesión">
						<input className="form-control" type="number" min={1} max={14} step={1} value={b.session_min_hours} disabled={bloqueado} onChange={(e) => set({ session_min_hours: e.target.value })} />
					</Campo>
					<Campo t="Estatus">
						<select className="form-select" value={b.status_id} disabled={bloqueado} onChange={(e) => set({ status_id: Number(e.target.value) })}>
							{estatuses.map((x) => <option key={x.id} value={x.id}>{x.name.charAt(0).toUpperCase() + x.name.slice(1)}</option>)}
						</select>
					</Campo>
					<div className="calCampo ancho">
						<small>Categorías</small>
						<div className="calChips">
							{categorias.map((c) => (
								<button type="button" key={c.id} disabled={bloqueado} aria-pressed={b.categories.some((x) => x.id === c.id)}
									className={"calChip" + (b.categories.some((x) => x.id === c.id) ? " activo" : "")} onClick={() => alternar(c)}>{nombreCategoria(c.name)}</button>
							))}
						</div>
					</div>
					<Campo t="Descripción" ancho>
						<textarea className="form-control" maxLength={255} value={b.description} disabled={bloqueado} onChange={(e) => set({ description: e.target.value })} />
					</Campo>
				</div>
			</form>
		</Ventana>
	);
};

// ------------------------------------------------------------------ edificio

export const EdificioVentana: React.FC<{
	campusId: number;
	edificio: Edificio | null;
	puedeEditar: boolean;
	puedeBorrar: boolean;
	onClose: () => void;
	onGuardado: (aviso: string) => void;
	onBorrar: (e: Edificio) => void;
}> = ({ campusId, edificio, puedeEditar, puedeBorrar, onClose, onGuardado, onBorrar }) => {
	const dispatch = useDispatch();
	const [nombre, setNombre] = useState(edificio?.name || "");
	const [descripcion, setDescripcion] = useState(edificio?.description || "");
	const [error, setError] = useState<string | null>(null);
	const [guardando, setGuardando] = useState(false);
	const soloLectura = !puedeEditar;

	const guardar = async () => {
		if (guardando || soloLectura) return;
		if (!nombre.trim()) { setError("Escribe el nombre del edificio."); return; }
		setGuardando(true);
		setError(null);
		dispatch(enableIdleLockSwitch());
		try {
			await BuildingsService.saveBuilding(campusId, { id: edificio ? edificio.id : null, name: nombre.trim(), description: descripcion });
			onGuardado(edificio ? "Edificio Guardado" : "Edificio Creado");
		} catch (e) {
			setError(motivoError(e, "Ya hay un edificio con ese nombre.", "No se pudo guardar el edificio."));
			setGuardando(false);
		} finally {
			dispatch(disableIdleLockSwitch());
		}
	};

	return (
		<Ventana titulo={edificio ? "Edificio" : "Nuevo Edificio"} onClose={onClose} busy={guardando} pie={<>
			{edificio && puedeBorrar ? <button type="button" className="btn btn-outline-secondary" disabled={guardando} onClick={() => onBorrar(edificio)}>Borrar Edificio</button> : null}
			<span className="calPieEspacio" />
			<button type="button" className="btn btn-outline-secondary" disabled={guardando} onClick={onClose}>{soloLectura ? "Cerrar" : "Cancelar"}</button>
			{soloLectura ? null : <button type="button" className="btn btn-primary" disabled={guardando} onClick={guardar}><FontAwesomeIcon icon={solid("check")} />&nbsp;Guardar</button>}
		</>}>
			{error ? <Aviso>{error}</Aviso> : null}
			<form onSubmit={(e) => { e.preventDefault(); guardar(); }}>
				<div className="calCampos">
					<Campo t="Nombre" ancho>
						<input className="form-control" type="text" maxLength={50} value={nombre} disabled={guardando || soloLectura} autoFocus={!edificio} onChange={(e) => setNombre(e.target.value)} />
					</Campo>
					<Campo t="Descripción" ancho>
						<textarea className="form-control" maxLength={255} value={descripcion} disabled={guardando || soloLectura} onChange={(e) => setDescripcion(e.target.value)} />
					</Campo>
				</div>
			</form>
		</Ventana>
	);
};
